import * as React from "react";
import { Typography } from "../ui/typography";
import searchIcon from "@/assets/svg/searchIcon.svg";
import searchIconLight from "@/assets/svg/searchIconLight.svg";
import SearchWithToggle from "../common/SearchInput";
import { Patient, clearSelectedPatient } from "@/store/slices/searchSlice";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { routes } from "@/utils/urls";
import { useFetchCard } from "@/hooks/useFetchCard";
import { getInitialsFromName } from "@/constants/utilityFunctions";
import { TruncatedTextTooltip } from "../common/TextTooltip";
import { useAppDispatch } from "@/store/hook";

interface DemographicsData {
  medicaidId: string;
  memberFirstName: string;
  memberLastName: string;
  agencyName: string;
}

const handleNullValues = (
  data: DemographicsData | null
): DemographicsData | null => {
  if (!data || typeof data !== "object") return data;

  const processed: any = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === "") {
      processed[key] = "-";
    } else {
      processed[key] = value;
    }
  }
  return processed as DemographicsData;
};

const DashboardHeader = () => {
  const [mode, setMode] = React.useState<"medicaid-id" | "dob-name">(
    "dob-name"
  );
  const [stableDemographics, setStableDemographics] =
    React.useState<DemographicsData | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const dispatch = useAppDispatch();
  // Fetch demographics data
  const { data: demographics } = useFetchCard<DemographicsData>({
    endpoint: routes.cards.demographics,
    memberId,
    enabled: !!memberId,
    processData: (rawData: unknown) =>
      handleNullValues((rawData as { data: DemographicsData }).data),
  });

  // Reset state when patient changes
  React.useEffect(() => {
    setStableDemographics(null);
    setIsLoading(true);
  }, [memberId]);

  // Update demographics when data arrives
  React.useEffect(() => {
    if (demographics) {
      setStableDemographics(demographics);
      setIsLoading(false);
    }
  }, [demographics]);

  const fullName = stableDemographics
    ? `${stableDemographics.memberFirstName} ${stableDemographics.memberLastName}`.trim()
    : "";
  const agencyName = stableDemographics?.agencyName || "";

  const showPatientInfo =
    memberId &&
    fullName &&
    fullName !== " " &&
    fullName !== "- -" &&
    location.pathname !== "/dashboard";

  const showSkeleton = memberId && isLoading;

  const handleModeChange = React.useCallback((value: string) => {
    setMode(value as "medicaid-id" | "dob-name");
  }, []);

  const handleSuggestionSelect = React.useCallback((_patient: Patient) => {
    // Navigation is already handled by SearchInput component
  }, []);

  // Memoize patient info to prevent unnecessary re-renders
  const PatientInfoContent = React.useMemo(() => {
    if (!showPatientInfo) return null;

    return (
      <div className='flex items-center gap-3'>
        <div className='w-[40px] h-[40px] bg-orange-100 rounded-full flex items-center justify-center border border-grey-300 dark:border-grey-300'>
          <Typography variant='semibold-16' className='text-orange-600'>
            {getInitialsFromName(fullName)}
          </Typography>
        </div>
        <div className='flex flex-col'>
          <TruncatedTextTooltip text={fullName} className='max-w-[200px] lg:max-w-[95px] xl-custom:max-w-[200px]'>
            <Typography
              variant='semibold-14'
              className='text-grey-800 dark:text-white leading-tight truncate'>
              {fullName}
            </Typography>
          </TruncatedTextTooltip>
          {agencyName && agencyName !== "-" && (
            <TruncatedTextTooltip text={agencyName} className='max-w-[200px] lg:max-w-[95px] xl-custom:max-w-[200px]'>
              <Typography
                variant='regular-14'
                className='text-grey-600 dark:text-grey-400 leading-tight truncate'>
                {agencyName}
              </Typography>
            </TruncatedTextTooltip>
          )}
        </div>
      </div>
    );
  }, [showPatientInfo, fullName, agencyName]);

  // Skeleton loader component
  const SkeletonLoader = React.useMemo(() => {
    if (!showSkeleton) return null;

    return (
      <div className='flex items-center gap-3'>
        <div className='w-[40px] h-[40px] bg-grey-200 dark:bg-grey-700 rounded-full animate-pulse'></div>
        <div className='flex flex-col gap-2'>
          <div className='h-4 w-32 bg-grey-200 dark:bg-grey-700 rounded animate-pulse'></div>
          <div className='h-3 w-24 bg-grey-200 dark:bg-grey-700 rounded animate-pulse'></div>
        </div>
      </div>
    );
  }, [showSkeleton]);

  React.useEffect(() => {
    if (location.pathname === "/dashboard" || location.pathname === "/") {
      dispatch(clearSelectedPatient());
    }
  }, [location.pathname, dispatch]);

  return (
    <div className='flex flex-col w-full py-1 sm:py-3 md:py-2 lg:py-2'>
      <div className='flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4 w-full'>
        {/* Left side - Dashboard title and Search */}
        <div className='flex flex-col sm:flex-row items-center gap-2 sm:gap-6 w-full sm:w-auto sm:flex-1'>
          <Typography
            variant='bold-22'
            className='hidden xl:block text-center text-3xl whitespace-nowrap'>
            Dashboard
          </Typography>

          <div className='flex justify-center items-center w-[90%] sm:w-full lg:max-w-5xl min-h-[48px] md:min-h-[60px]  [@media(min-width:1024px)_and_(max-width:1366px)]:w-[746px] '>
            <SearchWithToggle
              placeholder={
                mode === "medicaid-id"
                  ? "Search by Patient's Medicaid ID (e.g. NCXXXXXXXXX)"
                  : "Search by DOB and First 3 Letters of Patient's Last Name (e.g, 9/18/1970 GRi)"
              }
              icon={
                <>
                  <img
                    src={searchIcon}
                    alt='searchicon'
                    className='h-4 w-4 block dark:hidden'
                  />
                  <img
                    src={searchIconLight}
                    alt='searchicon'
                    className='h-4 w-4 hidden dark:block'
                  />
                </>
              }
              onSearchChange={() => undefined}
              onToggleChange={handleModeChange}
              onSuggestionSelect={handleSuggestionSelect}
              defaultToggle='dob-name'
              toggleOptions={[
                { value: "medicaid-id", label: "Medicaid ID" },
                { value: "dob-name", label: "Last Name + DOB" },
              ]}
            />
          </div>
        </div>

        <div className='hidden sm:flex items-center gap-3 md:hidden lg:flex min-w-[228px]'>
          {showSkeleton && (
            <>
              <div className='hidden sm:block md:hidden lg:hidden w-px h-10 bg-grey-300 dark:bg-grey-600'></div>
              {SkeletonLoader}
            </>
          )}
          {showPatientInfo && (
            <>
              <div className='hidden sm:block md:hidden lg:hidden w-px h-10 bg-grey-300 dark:bg-grey-600'></div>
              {PatientInfoContent}
            </>
          )}
        </div>
      </div>

      {/* Patient info on new line - visible on mobile and md screens, hidden on sm and lg */}
      {(showSkeleton || showPatientInfo) && (
        <div className='block sm:hidden md:block lg:hidden w-full mt-3'>
          {/* Full-width divider */}
          <div
            className='
        relative left-1/2 right-1/2
        w-screen -translate-x-1/2
        border-t border-grey-300 dark:border-primary-700
      '
          />

          <div className='pt-3 px-4 sm:px-5 md:px-0'>
            {showSkeleton ? SkeletonLoader : PatientInfoContent}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHeader;
